import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, Target, Zap, Rocket, TrendingUp, Award, 
  CheckCircle, Clock, User, Loader2, Save, Plus, Trash2, Sparkles
} from "lucide-react";

export const ConsultantRapidDetailPage = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [client, setClient] = useState(null);
  
  // Session completion forms
  const [session1Form, setSession1Form] = useState({
    consultant_notes: "",
    consultant_recommendations: "",
    constraint_map: { decision_bottlenecks: "", behavioral_patterns: "", execution_friction: "" },
    assumptions: [{ assumption: "", where_shows_up: "", limits_execution: "" }]
  });
  
  const [session2Form, setSession2Form] = useState({
    consultant_notes: "",
    consultant_recommendations: "",
    decision_filters: { decide_by: "", no_longer_delay: "", prioritize: "" },
    leadership_standards: { boundaries: "", non_negotiables: "", expectations: "" }
  });
  
  const [session3Form, setSession3Form] = useState({
    consultant_notes: "",
    consultant_recommendations: "",
    execution_priorities: { priority_decisions: "", immediate_actions: "", delegations: "" },
    review_framework: { weekly_check_questions: "", red_flags: "", recalibration_cues: "" }
  });

  const [momentumForm, setMomentumForm] = useState({
    active_decisions: [{ decision: "", status: "", due_date: "" }]
  });

  const [completionForm, setCompletionForm] = useState({
    outcomes_summary: {
      decisions_clarified: "",
      constraints_removed: "",
      standards_installed: "",
      momentum_activated: ""
    }
  });

  useEffect(() => {
    fetchEnrollment();
  }, [clientId]);

  const fetchEnrollment = async () => {
    try {
      const response = await authFetch(`${API}/consultant/rapid/${clientId}`, {  });
      if (response.ok) {
        const data = await response.json();
        setEnrollment(data);
        setClient(data.client);
        
        // Pre-fill forms from existing data
        if (data.session_1?.outputs) {
          setSession1Form(prev => ({
            ...prev,
            consultant_notes: data.session_1.consultant_notes || "",
            consultant_recommendations: data.session_1.consultant_recommendations || data.session_1.outputs?.consultant_recommendations || "",
            constraint_map: data.session_1.outputs.constraint_map || prev.constraint_map,
            assumptions: data.session_1.outputs.assumptions?.length > 0 
              ? data.session_1.outputs.assumptions 
              : prev.assumptions
          }));
        }
        if (data.session_2?.outputs) {
          setSession2Form(prev => ({
            ...prev,
            consultant_notes: data.session_2.consultant_notes || "",
            consultant_recommendations: data.session_2.consultant_recommendations || data.session_2.outputs?.consultant_recommendations || "",
            decision_filters: data.session_2.outputs.decision_filters || prev.decision_filters,
            leadership_standards: data.session_2.outputs.leadership_standards || prev.leadership_standards
          }));
        }
        if (data.session_3?.outputs) {
          setSession3Form(prev => ({
            ...prev,
            consultant_notes: data.session_3.consultant_notes || "",
            consultant_recommendations: data.session_3.consultant_recommendations || data.session_3.outputs?.consultant_recommendations || "",
            execution_priorities: data.session_3.outputs.execution_priorities || prev.execution_priorities,
            review_framework: data.session_3.outputs.review_framework || prev.review_framework
          }));
        }
        if (data.momentum_plan?.active_decisions?.length > 0) {
          setMomentumForm(prev => ({
            ...prev,
            active_decisions: data.momentum_plan.active_decisions
          }));
        }
        if (data.completion_data?.outcomes_summary) {
          setCompletionForm(prev => ({
            ...prev,
            outcomes_summary: data.completion_data.outcomes_summary
          }));
        }
      }
    } catch (_error) {
      console.error("Error fetching enrollment:", _error);
      toast.error("Failed to load client data");
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSession = async (sessionNumber, formData) => {
    setSaving(true);
    try {
      const outputs = {};
      
      if (sessionNumber === 1) {
        outputs.constraint_map = formData.constraint_map;
        outputs.assumptions = formData.assumptions.filter(a => a.assumption);
        outputs.consultant_recommendations = formData.consultant_recommendations;
      } else if (sessionNumber === 2) {
        outputs.decision_filters = formData.decision_filters;
        outputs.leadership_standards = formData.leadership_standards;
        outputs.consultant_recommendations = formData.consultant_recommendations;
      } else if (sessionNumber === 3) {
        outputs.execution_priorities = formData.execution_priorities;
        outputs.review_framework = formData.review_framework;
        outputs.consultant_recommendations = formData.consultant_recommendations;
      }

      const response = await authFetch(`${API}/consultant/rapid/${clientId}/session/${sessionNumber}/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          consultant_notes: formData.consultant_notes,
          outputs
        })
      });

      if (response.ok) {
        toast.success(`Session ${sessionNumber} marked as complete!`);
        fetchEnrollment();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to complete session");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = async (sessionNumber, formData) => {
    setSaving(true);
    try {
      const outputs = {};
      if (sessionNumber === 1) {
        outputs.constraint_map = formData.constraint_map;
        outputs.assumptions = formData.assumptions?.filter(a => a.assumption) || [];
        outputs.consultant_recommendations = formData.consultant_recommendations;
      } else if (sessionNumber === 2) {
        outputs.decision_filters = formData.decision_filters;
        outputs.leadership_standards = formData.leadership_standards;
        outputs.consultant_recommendations = formData.consultant_recommendations;
      } else if (sessionNumber === 3) {
        outputs.execution_priorities = formData.execution_priorities;
        outputs.review_framework = formData.review_framework;
        outputs.consultant_recommendations = formData.consultant_recommendations;
      }

      const response = await authFetch(`${API}/consultant/rapid/${clientId}/session/${sessionNumber}/save-notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ consultant_notes: formData.consultant_notes, outputs })
      });

      if (response.ok) {
        toast.success(`Session ${sessionNumber} notes saved!`);
        fetchEnrollment();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save notes");
      }
    } catch (_error) {
      toast.error("Failed to save notes");
    } finally {
      setSaving(false);
    }
  };


  const handleUpdateMomentum = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API}/consultant/rapid/${clientId}/momentum`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          active_decisions: momentumForm.active_decisions.filter(d => d.decision)
        })
      });

      if (response.ok) {
        toast.success("Momentum plan updated!");
        fetchEnrollment();
      } else {
        toast.error("Failed to update momentum plan");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteEngagement = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API}/consultant/rapid/${clientId}/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          outcomes_summary: completionForm.outcomes_summary
        })
      });

      if (response.ok) {
        toast.success("R.A.P.I.D.™ engagement marked as complete!");
        fetchEnrollment();
      } else {
        toast.error("Failed to complete engagement");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSaving(false);
    }
  };

  // AI Generation Functions
  const handleAiGenerateSession = async (sessionNum) => {
    setAiGenerating(true);
    try {
      const response = await authFetch(`${API}/ai/rapid/session/${sessionNum}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ client_id: clientId })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.outputs) {
          if (sessionNum === 1) {
            setSession1Form(prev => ({
              ...prev,
              constraint_map: data.outputs.constraint_map || prev.constraint_map,
              assumptions: data.outputs.assumptions?.length > 0 ? data.outputs.assumptions : prev.assumptions
            }));
          } else if (sessionNum === 2) {
            setSession2Form(prev => ({
              ...prev,
              decision_filters: data.outputs.decision_filters || prev.decision_filters,
              leadership_standards: data.outputs.leadership_standards || prev.leadership_standards
            }));
          } else if (sessionNum === 3) {
            setSession3Form(prev => ({
              ...prev,
              execution_priorities: data.outputs.execution_priorities || prev.execution_priorities,
              review_framework: data.outputs.review_framework || prev.review_framework
            }));
          }
          toast.success(`AI generated Session ${sessionNum} outputs! Review and edit as needed.`);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "AI generation failed");
      }
    } catch (_error) {
      toast.error("AI generation failed");
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAiGenerateMomentum = async () => {
    setAiGenerating(true);
    try {
      const response = await authFetch(`${API}/ai/rapid/momentum/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ client_id: clientId })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.momentum_plan?.active_decisions) {
          setMomentumForm(prev => ({
            ...prev,
            active_decisions: data.momentum_plan.active_decisions
          }));
          toast.success("AI generated Momentum Plan! Review and edit as needed.");
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "AI generation failed");
      }
    } catch (_error) {
      toast.error("AI generation failed");
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAiGenerateCompletion = async () => {
    setAiGenerating(true);
    try {
      const response = await authFetch(`${API}/ai/rapid/completion/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ client_id: clientId })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.completion?.outcomes_summary) {
          setCompletionForm(prev => ({
            ...prev,
            outcomes_summary: data.completion.outcomes_summary
          }));
          toast.success("AI generated Completion Summary! Review and edit as needed.");
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "AI generation failed");
      }
    } catch (_error) {
      toast.error("AI generation failed");
    } finally {
      setAiGenerating(false);
    }
  };

  const addAssumption = () => {
    setSession1Form(prev => ({
      ...prev,
      assumptions: [...prev.assumptions, { assumption: "", where_shows_up: "", limits_execution: "" }]
    }));
  };

  const removeAssumption = (index) => {
    setSession1Form(prev => ({
      ...prev,
      assumptions: prev.assumptions.filter((_, i) => i !== index)
    }));
  };

  const addActiveDecision = () => {
    setMomentumForm(prev => ({
      ...prev,
      active_decisions: [...prev.active_decisions, { decision: "", status: "", due_date: "" }]
    }));
  };

  const removeActiveDecision = (index) => {
    setMomentumForm(prev => ({
      ...prev,
      active_decisions: prev.active_decisions.filter((_, i) => i !== index)
    }));
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      intake_pending: { label: "Intake Pending", color: "bg-yellow-100 text-yellow-700" },
      intake_complete: { label: "Intake Complete", color: "bg-blue-100 text-blue-700" },
      session_1: { label: "Session 1", color: "bg-purple-100 text-purple-700" },
      session_2: { label: "Session 2", color: "bg-indigo-100 text-indigo-700" },
      session_3: { label: "Session 3", color: "bg-orange-100 text-orange-700" },
      momentum: { label: "Momentum", color: "bg-teal-100 text-teal-700" },
      completed: { label: "Completed", color: "bg-green-100 text-green-700" }
    };
    const s = statusMap[status] || { label: status, color: "bg-gray-100 text-gray-700" };
    return <Badge className={s.color}>{s.label}</Badge>;
  };

  const parsePreSessionResponse = (response) => {
    if (!response) return null;
    try {
      return JSON.parse(response);
    } catch {
      return { raw: response };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="consultant-rapid-detail">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/consultant")} className="text-neutral-600">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <div>
                <div className="flex items-center space-x-3">
                  <h1 className="text-xl font-semibold text-neutral-900">R.A.P.I.D.™ Management</h1>
                  {getStatusBadge(enrollment?.status)}
                </div>
                <p className="text-sm text-neutral-500 flex items-center">
                  <User className="w-4 h-4 mr-1" />
                  {client?.name} ({client?.email})
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid grid-cols-6 w-full">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="session1">Session 1</TabsTrigger>
            <TabsTrigger value="session2">Session 2</TabsTrigger>
            <TabsTrigger value="session3">Session 3</TabsTrigger>
            <TabsTrigger value="momentum">Momentum</TabsTrigger>
            <TabsTrigger value="completion">Complete</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Client Progress Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-3">Engagement Status</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                        <span>Intake</span>
                        {enrollment?.intake_data ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <Clock className="w-5 h-5 text-yellow-500" />
                        )}
                      </div>
                      <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                        <span>Session 1</span>
                        {enrollment?.session_1?.completed ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : enrollment?.session_1?.pre_session_response ? (
                          <Badge className="bg-blue-100 text-blue-700">Ready</Badge>
                        ) : (
                          <Clock className="w-5 h-5 text-neutral-300" />
                        )}
                      </div>
                      <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                        <span>Session 2</span>
                        {enrollment?.session_2?.completed ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : enrollment?.session_2?.pre_session_response ? (
                          <Badge className="bg-blue-100 text-blue-700">Ready</Badge>
                        ) : (
                          <Clock className="w-5 h-5 text-neutral-300" />
                        )}
                      </div>
                      <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                        <span>Session 3</span>
                        {enrollment?.session_3?.completed ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : enrollment?.session_3?.pre_session_response ? (
                          <Badge className="bg-blue-100 text-blue-700">Ready</Badge>
                        ) : (
                          <Clock className="w-5 h-5 text-neutral-300" />
                        )}
                      </div>
                      <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                        <span>Momentum Plan</span>
                        {enrollment?.momentum_plan?.commitment_acknowledged ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <Clock className="w-5 h-5 text-neutral-300" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {enrollment?.intake_data && (
                    <div>
                      <h4 className="font-medium mb-3">Intake Summary</h4>
                      <div className="space-y-3 text-sm">
                        <div>
                          <p className="text-neutral-500">Role</p>
                          <p className="font-medium">{enrollment.intake_data.current_role}</p>
                        </div>
                        <div>
                          <p className="text-neutral-500">Business Model</p>
                          <p className="font-medium">{enrollment.intake_data.business_model}</p>
                        </div>
                        <div>
                          <p className="text-neutral-500">Growth Stage</p>
                          <p className="font-medium">{enrollment.intake_data.growth_stage}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Session 1 Tab */}
          <TabsContent value="session1" className="space-y-6">
            {/* Client's Pre-Session Response */}
            {enrollment?.session_1?.pre_session_response && (
              <Card>
                <CardHeader className="bg-emerald-50 border-b">
                  <CardTitle className="text-lg">Client&apos;s Pre-Session Reflection</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {(() => {
                    const data = parsePreSessionResponse(enrollment.session_1.pre_session_response);
                    if (!data) return <p className="text-neutral-500">No response submitted</p>;
                    return (
                      <div className="space-y-4">
                        {data.top_3_decisions && (
                          <div>
                            <p className="font-medium text-neutral-700">Top 3 energy-consuming decisions:</p>
                            <p className="mt-1 text-neutral-600 bg-neutral-50 p-3 rounded">{data.top_3_decisions}</p>
                          </div>
                        )}
                        {data.delaying_decision && (
                          <div>
                            <p className="font-medium text-neutral-700">Decision being delayed:</p>
                            <p className="mt-1 text-neutral-600 bg-neutral-50 p-3 rounded">{data.delaying_decision}</p>
                          </div>
                        )}
                        {data.over_involved && (
                          <div>
                            <p className="font-medium text-neutral-700">Areas of over-involvement:</p>
                            <p className="mt-1 text-neutral-600 bg-neutral-50 p-3 rounded">{data.over_involved}</p>
                          </div>
                        )}
                        {data.results_not_matching_effort && (
                          <div>
                            <p className="font-medium text-neutral-700">Results not matching effort:</p>
                            <p className="mt-1 text-neutral-600 bg-neutral-50 p-3 rounded">{data.results_not_matching_effort}</p>
                          </div>
                        )}
                        {data.heavier_than_should && (
                          <div>
                            <p className="font-medium text-neutral-700">What feels heavier than it should:</p>
                            <p className="mt-1 text-neutral-600 bg-neutral-50 p-3 rounded">{data.heavier_than_should}</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Consultant Form */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Session 1 Completion</CardTitle>
                    <CardDescription>
                      Fill in the outputs from your session to unlock Session 2 for the client.
                    </CardDescription>
                  </div>
                  {!enrollment?.session_1?.completed && enrollment?.session_1?.pre_session_response && (
                    <Button
                      onClick={() => handleAiGenerateSession(1)}
                      disabled={aiGenerating}
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                      data-testid="ai-generate-session1"
                    >
                      {aiGenerating ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      AI Assist
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Consultant Notes</Label>
                  <Textarea
                    value={session1Form.consultant_notes}
                    onChange={(e) => setSession1Form(prev => ({ ...prev, consultant_notes: e.target.value }))}
                    placeholder="Your private notes from the session..."
                    className="min-h-24"
                    disabled={enrollment?.session_1?.completed}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-emerald-500" />
                    Consultant Recommendations
                    <span className="text-xs text-neutral-400 font-normal">(shared with client after session completes)</span>
                  </Label>
                  <Textarea
                    value={session1Form.consultant_recommendations}
                    onChange={(e) => setSession1Form(prev => ({ ...prev, consultant_recommendations: e.target.value }))}
                    placeholder="Key recommendations, action items, and strategic advice for the client..."
                    className="min-h-32 border-emerald-200 focus:ring-emerald-500"
                    disabled={enrollment?.session_1?.completed}
                  />
                </div>

                <Separator />

                <div>
                  <h4 className="font-medium mb-4">Constraint Map</h4>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Decision Bottlenecks</Label>
                      <Textarea
                        value={session1Form.constraint_map.decision_bottlenecks}
                        onChange={(e) => setSession1Form(prev => ({
                          ...prev,
                          constraint_map: { ...prev.constraint_map, decision_bottlenecks: e.target.value }
                        }))}
                        placeholder="Identify the key decision bottlenecks..."
                        disabled={enrollment?.session_1?.completed}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Behavioral Patterns</Label>
                      <Textarea
                        value={session1Form.constraint_map.behavioral_patterns}
                        onChange={(e) => setSession1Form(prev => ({
                          ...prev,
                          constraint_map: { ...prev.constraint_map, behavioral_patterns: e.target.value }
                        }))}
                        placeholder="Document observed behavioral patterns..."
                        disabled={enrollment?.session_1?.completed}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Execution Friction Points</Label>
                      <Textarea
                        value={session1Form.constraint_map.execution_friction}
                        onChange={(e) => setSession1Form(prev => ({
                          ...prev,
                          constraint_map: { ...prev.constraint_map, execution_friction: e.target.value }
                        }))}
                        placeholder="List execution friction points..."
                        disabled={enrollment?.session_1?.completed}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium">Operating Assumptions</h4>
                    {!enrollment?.session_1?.completed && (
                      <Button variant="outline" size="sm" onClick={addAssumption}>
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    )}
                  </div>
                  <div className="space-y-4">
                    {session1Form.assumptions.map((item, idx) => (
                      <div key={idx} className="p-4 border rounded-lg space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-neutral-500">Assumption {idx + 1}</span>
                          {!enrollment?.session_1?.completed && session1Form.assumptions.length > 1 && (
                            <Button variant="ghost" size="sm" onClick={() => removeAssumption(idx)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                        <Input
                          value={item.assumption}
                          onChange={(e) => {
                            const updated = [...session1Form.assumptions];
                            updated[idx].assumption = e.target.value;
                            setSession1Form(prev => ({ ...prev, assumptions: updated }));
                          }}
                          placeholder="The assumption..."
                          disabled={enrollment?.session_1?.completed}
                        />
                        <Input
                          value={item.where_shows_up}
                          onChange={(e) => {
                            const updated = [...session1Form.assumptions];
                            updated[idx].where_shows_up = e.target.value;
                            setSession1Form(prev => ({ ...prev, assumptions: updated }));
                          }}
                          placeholder="Where it shows up..."
                          disabled={enrollment?.session_1?.completed}
                        />
                        <Input
                          value={item.limits_execution}
                          onChange={(e) => {
                            const updated = [...session1Form.assumptions];
                            updated[idx].limits_execution = e.target.value;
                            setSession1Form(prev => ({ ...prev, assumptions: updated }));
                          }}
                          placeholder="How it limits execution..."
                          disabled={enrollment?.session_1?.completed}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {!enrollment?.session_1?.completed && (
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleSaveNotes(1, session1Form)}
                      variant="outline"
                      className="flex-1 border-blue-300 text-blue-600 hover:bg-blue-50"
                      disabled={saving}
                      data-testid="save-notes-session-1"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Notes
                    </Button>
                    <Button
                      onClick={() => handleCompleteSession(1, session1Form)}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      disabled={saving || !enrollment?.session_1?.pre_session_response}
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Complete & Unlock Session 2
                    </Button>
                  </div>
                )}

                {enrollment?.session_1?.completed && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
                    <p className="text-green-800 font-medium">Session 1 Completed</p>
                    <p className="text-green-600 text-sm">
                      Completed on {new Date(enrollment.session_1.completed_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Session 2 Tab */}
          <TabsContent value="session2" className="space-y-6">
            {enrollment?.session_2?.pre_session_response && (
              <Card>
                <CardHeader className="bg-blue-50 border-b">
                  <CardTitle className="text-lg">Client&apos;s Pre-Session Reflection</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {(() => {
                    const data = parsePreSessionResponse(enrollment.session_2.pre_session_response);
                    if (!data) return <p className="text-neutral-500">No response submitted</p>;
                    return (
                      <div className="space-y-4">
                        {data.unresolved_decision && (
                          <div>
                            <p className="font-medium text-neutral-700">Unresolved decision:</p>
                            <p className="mt-1 text-neutral-600 bg-neutral-50 p-3 rounded">{data.unresolved_decision}</p>
                          </div>
                        )}
                        {data.outdated_assumption && (
                          <div>
                            <p className="font-medium text-neutral-700">Outdated assumption recognized:</p>
                            <p className="mt-1 text-neutral-600 bg-neutral-50 p-3 rounded">{data.outdated_assumption}</p>
                          </div>
                        )}
                        {data.boundary_to_change && (
                          <div>
                            <p className="font-medium text-neutral-700">Standard/boundary to change:</p>
                            <p className="mt-1 text-neutral-600 bg-neutral-50 p-3 rounded">{data.boundary_to_change}</p>
                          </div>
                        )}
                        {data.trust_decision && (
                          <div>
                            <p className="font-medium text-neutral-700">Decision if fully trusted self:</p>
                            <p className="mt-1 text-neutral-600 bg-neutral-50 p-3 rounded">{data.trust_decision}</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Session 2 Completion</CardTitle>
                    <CardDescription>
                      Fill in the decision filters and leadership standards from your session.
                    </CardDescription>
                  </div>
                  {!enrollment?.session_2?.completed && enrollment?.session_1?.completed && enrollment?.session_2?.pre_session_response && (
                    <Button
                      onClick={() => handleAiGenerateSession(2)}
                      disabled={aiGenerating}
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                      data-testid="ai-generate-session2"
                    >
                      {aiGenerating ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      AI Assist
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Consultant Notes</Label>
                  <Textarea
                    value={session2Form.consultant_notes}
                    onChange={(e) => setSession2Form(prev => ({ ...prev, consultant_notes: e.target.value }))}
                    placeholder="Your private notes from the session..."
                    className="min-h-24"
                    disabled={enrollment?.session_2?.completed}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-blue-500" />
                    Consultant Recommendations
                    <span className="text-xs text-neutral-400 font-normal">(shared with client after session completes)</span>
                  </Label>
                  <Textarea
                    value={session2Form.consultant_recommendations}
                    onChange={(e) => setSession2Form(prev => ({ ...prev, consultant_recommendations: e.target.value }))}
                    placeholder="Key recommendations, action items, and strategic advice for the client..."
                    className="min-h-32 border-blue-200 focus:ring-blue-500"
                    disabled={enrollment?.session_2?.completed}
                  />
                </div>

                <Separator />

                <div>
                  <h4 className="font-medium mb-4">Decision Filters Installed</h4>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>&quot;At this level, I decide by…&quot;</Label>
                      <Textarea
                        value={session2Form.decision_filters.decide_by}
                        onChange={(e) => setSession2Form(prev => ({
                          ...prev,
                          decision_filters: { ...prev.decision_filters, decide_by: e.target.value }
                        }))}
                        disabled={enrollment?.session_2?.completed}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>&quot;I no longer delay decisions when…&quot;</Label>
                      <Textarea
                        value={session2Form.decision_filters.no_longer_delay}
                        onChange={(e) => setSession2Form(prev => ({
                          ...prev,
                          decision_filters: { ...prev.decision_filters, no_longer_delay: e.target.value }
                        }))}
                        disabled={enrollment?.session_2?.completed}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>&quot;This role requires me to prioritize…&quot;</Label>
                      <Textarea
                        value={session2Form.decision_filters.prioritize}
                        onChange={(e) => setSession2Form(prev => ({
                          ...prev,
                          decision_filters: { ...prev.decision_filters, prioritize: e.target.value }
                        }))}
                        disabled={enrollment?.session_2?.completed}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h4 className="font-medium mb-4">Updated Leadership Standards</h4>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Boundaries</Label>
                      <Textarea
                        value={session2Form.leadership_standards.boundaries}
                        onChange={(e) => setSession2Form(prev => ({
                          ...prev,
                          leadership_standards: { ...prev.leadership_standards, boundaries: e.target.value }
                        }))}
                        disabled={enrollment?.session_2?.completed}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Non-negotiables</Label>
                      <Textarea
                        value={session2Form.leadership_standards.non_negotiables}
                        onChange={(e) => setSession2Form(prev => ({
                          ...prev,
                          leadership_standards: { ...prev.leadership_standards, non_negotiables: e.target.value }
                        }))}
                        disabled={enrollment?.session_2?.completed}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Expectations of Self and Others</Label>
                      <Textarea
                        value={session2Form.leadership_standards.expectations}
                        onChange={(e) => setSession2Form(prev => ({
                          ...prev,
                          leadership_standards: { ...prev.leadership_standards, expectations: e.target.value }
                        }))}
                        disabled={enrollment?.session_2?.completed}
                      />
                    </div>
                  </div>
                </div>

                {!enrollment?.session_2?.completed && enrollment?.session_1?.completed && (
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleSaveNotes(2, session2Form)}
                      variant="outline"
                      className="flex-1 border-blue-300 text-blue-600 hover:bg-blue-50"
                      disabled={saving}
                      data-testid="save-notes-session-2"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Notes
                    </Button>
                    <Button
                      onClick={() => handleCompleteSession(2, session2Form)}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      disabled={saving || !enrollment?.session_2?.pre_session_response}
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Complete & Unlock Session 3
                    </Button>
                  </div>
                )}

                {enrollment?.session_2?.completed && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
                    <p className="text-green-800 font-medium">Session 2 Completed</p>
                  </div>
                )}

                {!enrollment?.session_1?.completed && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                    <Clock className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
                    <p className="text-yellow-800">Complete Session 1 first</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Session 3 Tab */}
          <TabsContent value="session3" className="space-y-6">
            {enrollment?.session_3?.pre_session_response && (
              <Card>
                <CardHeader className="bg-orange-50 border-b">
                  <CardTitle className="text-lg">Client&apos;s Pre-Session Reflection</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {(() => {
                    const data = parsePreSessionResponse(enrollment.session_3.pre_session_response);
                    if (!data) return <p className="text-neutral-500">No response submitted</p>;
                    return (
                      <div className="space-y-4">
                        {data.decisions_shifted && (
                          <div>
                            <p className="font-medium text-neutral-700">Decisions that have shifted:</p>
                            <p className="mt-1 text-neutral-600 bg-neutral-50 p-3 rounded">{data.decisions_shifted}</p>
                          </div>
                        )}
                        {data.execution_easier && (
                          <div>
                            <p className="font-medium text-neutral-700">Where execution feels easier:</p>
                            <p className="mt-1 text-neutral-600 bg-neutral-50 p-3 rounded">{data.execution_easier}</p>
                          </div>
                        )}
                        {data.remaining_friction && (
                          <div>
                            <p className="font-medium text-neutral-700">Remaining friction:</p>
                            <p className="mt-1 text-neutral-600 bg-neutral-50 p-3 rounded">{data.remaining_friction}</p>
                          </div>
                        )}
                        {data.next_30_days && (
                          <div>
                            <p className="font-medium text-neutral-700">Next 30 days milestones:</p>
                            <p className="mt-1 text-neutral-600 bg-neutral-50 p-3 rounded">{data.next_30_days}</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Session 3 Completion</CardTitle>
                    <CardDescription>
                      Fill in execution priorities and decision review framework.
                    </CardDescription>
                  </div>
                  {!enrollment?.session_3?.completed && enrollment?.session_2?.completed && enrollment?.session_3?.pre_session_response && (
                    <Button
                      onClick={() => handleAiGenerateSession(3)}
                      disabled={aiGenerating}
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                      data-testid="ai-generate-session3"
                    >
                      {aiGenerating ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      AI Assist
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Consultant Notes</Label>
                  <Textarea
                    value={session3Form.consultant_notes}
                    onChange={(e) => setSession3Form(prev => ({ ...prev, consultant_notes: e.target.value }))}
                    placeholder="Your private notes from the session..."
                    className="min-h-24"
                    disabled={enrollment?.session_3?.completed}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-orange-500" />
                    Consultant Recommendations
                    <span className="text-xs text-neutral-400 font-normal">(shared with client after session completes)</span>
                  </Label>
                  <Textarea
                    value={session3Form.consultant_recommendations}
                    onChange={(e) => setSession3Form(prev => ({ ...prev, consultant_recommendations: e.target.value }))}
                    placeholder="Key recommendations, action items, and strategic advice for the client..."
                    className="min-h-32 border-orange-200 focus:ring-orange-500"
                    disabled={enrollment?.session_3?.completed}
                  />
                </div>

                <Separator />

                <div>
                  <h4 className="font-medium mb-4">Execution Priorities Locked</h4>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Priority Decisions</Label>
                      <Textarea
                        value={session3Form.execution_priorities.priority_decisions}
                        onChange={(e) => setSession3Form(prev => ({
                          ...prev,
                          execution_priorities: { ...prev.execution_priorities, priority_decisions: e.target.value }
                        }))}
                        disabled={enrollment?.session_3?.completed}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Immediate Actions</Label>
                      <Textarea
                        value={session3Form.execution_priorities.immediate_actions}
                        onChange={(e) => setSession3Form(prev => ({
                          ...prev,
                          execution_priorities: { ...prev.execution_priorities, immediate_actions: e.target.value }
                        }))}
                        disabled={enrollment?.session_3?.completed}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Delegations or Removals</Label>
                      <Textarea
                        value={session3Form.execution_priorities.delegations}
                        onChange={(e) => setSession3Form(prev => ({
                          ...prev,
                          execution_priorities: { ...prev.execution_priorities, delegations: e.target.value }
                        }))}
                        disabled={enrollment?.session_3?.completed}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h4 className="font-medium mb-4">Decision Review Framework</h4>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Weekly Decision Check Questions</Label>
                      <Textarea
                        value={session3Form.review_framework.weekly_check_questions}
                        onChange={(e) => setSession3Form(prev => ({
                          ...prev,
                          review_framework: { ...prev.review_framework, weekly_check_questions: e.target.value }
                        }))}
                        disabled={enrollment?.session_3?.completed}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Red Flags</Label>
                      <Textarea
                        value={session3Form.review_framework.red_flags}
                        onChange={(e) => setSession3Form(prev => ({
                          ...prev,
                          review_framework: { ...prev.review_framework, red_flags: e.target.value }
                        }))}
                        disabled={enrollment?.session_3?.completed}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Recalibration Cues</Label>
                      <Textarea
                        value={session3Form.review_framework.recalibration_cues}
                        onChange={(e) => setSession3Form(prev => ({
                          ...prev,
                          review_framework: { ...prev.review_framework, recalibration_cues: e.target.value }
                        }))}
                        disabled={enrollment?.session_3?.completed}
                      />
                    </div>
                  </div>
                </div>

                {!enrollment?.session_3?.completed && enrollment?.session_2?.completed && (
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleSaveNotes(3, session3Form)}
                      variant="outline"
                      className="flex-1 border-orange-300 text-orange-600 hover:bg-orange-50"
                      disabled={saving}
                      data-testid="save-notes-session-3"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Notes
                    </Button>
                    <Button
                      onClick={() => handleCompleteSession(3, session3Form)}
                      className="flex-1 bg-orange-600 hover:bg-orange-700"
                      disabled={saving || !enrollment?.session_3?.pre_session_response}
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Complete & Unlock Momentum
                    </Button>
                  </div>
                )}

                {enrollment?.session_3?.completed && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
                    <p className="text-green-800 font-medium">Session 3 Completed</p>
                  </div>
                )}

                {!enrollment?.session_2?.completed && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                    <Clock className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
                    <p className="text-yellow-800">Complete Session 2 first</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Momentum Tab */}
          <TabsContent value="momentum" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Momentum Plan - Active Decisions</CardTitle>
                    <CardDescription>
                      Add the active decisions that require follow-through.
                    </CardDescription>
                  </div>
                  {enrollment?.session_3?.completed && (
                    <Button
                      onClick={handleAiGenerateMomentum}
                      disabled={aiGenerating}
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                      data-testid="ai-generate-momentum"
                    >
                      {aiGenerating ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      AI Generate
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={addActiveDecision}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Decision
                  </Button>
                </div>

                <div className="space-y-4">
                  {momentumForm.active_decisions.map((item, idx) => (
                    <div key={idx} className="p-4 border rounded-lg space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-neutral-500">Decision {idx + 1}</span>
                        {momentumForm.active_decisions.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => removeActiveDecision(idx)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                      <Input
                        value={item.decision}
                        onChange={(e) => {
                          const updated = [...momentumForm.active_decisions];
                          updated[idx].decision = e.target.value;
                          setMomentumForm(prev => ({ ...prev, active_decisions: updated }));
                        }}
                        placeholder="The decision..."
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          value={item.status}
                          onChange={(e) => {
                            const updated = [...momentumForm.active_decisions];
                            updated[idx].status = e.target.value;
                            setMomentumForm(prev => ({ ...prev, active_decisions: updated }));
                          }}
                          placeholder="Status..."
                        />
                        <Input
                          type="date"
                          value={item.due_date}
                          onChange={(e) => {
                            const updated = [...momentumForm.active_decisions];
                            updated[idx].due_date = e.target.value;
                            setMomentumForm(prev => ({ ...prev, active_decisions: updated }));
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={handleUpdateMomentum}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  disabled={saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Momentum Plan
                </Button>
              </CardContent>
            </Card>

            {enrollment?.momentum_plan && (
              <Card>
                <CardHeader className="bg-slate-50 border-b">
                  <CardTitle className="text-lg">Client&apos;s Execution Rhythm</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  {enrollment.momentum_plan.weekly_review_decisions && (
                    <div>
                      <p className="font-medium text-neutral-700">Weekly Review Decisions:</p>
                      <p className="mt-1 text-neutral-600 bg-neutral-50 p-3 rounded">{enrollment.momentum_plan.weekly_review_decisions}</p>
                    </div>
                  )}
                  {enrollment.momentum_plan.standards_to_protect && (
                    <div>
                      <p className="font-medium text-neutral-700">Standards to Protect:</p>
                      <p className="mt-1 text-neutral-600 bg-neutral-50 p-3 rounded">{enrollment.momentum_plan.standards_to_protect}</p>
                    </div>
                  )}
                  {enrollment.momentum_plan.recalibration_signals && (
                    <div>
                      <p className="font-medium text-neutral-700">Recalibration Signals:</p>
                      <p className="mt-1 text-neutral-600 bg-neutral-50 p-3 rounded">{enrollment.momentum_plan.recalibration_signals}</p>
                    </div>
                  )}
                  {enrollment.momentum_plan.commitment_acknowledged && (
                    <div className="bg-green-50 p-3 rounded-lg">
                      <p className="text-green-700 flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Client acknowledged commitment on {new Date(enrollment.momentum_plan.commitment_date).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Completion Tab */}
          <TabsContent value="completion" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Complete R.A.P.I.D.™ Engagement</CardTitle>
                    <CardDescription>
                      Summarize the outcomes and finalize the engagement.
                    </CardDescription>
                  </div>
                  {enrollment?.status !== "completed" && enrollment?.session_3?.completed && (
                    <Button
                      onClick={handleAiGenerateCompletion}
                      disabled={aiGenerating}
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                      data-testid="ai-generate-completion"
                    >
                      {aiGenerating ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      AI Summarize
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Decisions Clarified</Label>
                    <Textarea
                      value={completionForm.outcomes_summary.decisions_clarified}
                      onChange={(e) => setCompletionForm(prev => ({
                        ...prev,
                        outcomes_summary: { ...prev.outcomes_summary, decisions_clarified: e.target.value }
                      }))}
                      placeholder="Summary of decisions clarified..."
                      disabled={enrollment?.status === "completed"}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Constraints Removed</Label>
                    <Textarea
                      value={completionForm.outcomes_summary.constraints_removed}
                      onChange={(e) => setCompletionForm(prev => ({
                        ...prev,
                        outcomes_summary: { ...prev.outcomes_summary, constraints_removed: e.target.value }
                      }))}
                      placeholder="Summary of constraints removed..."
                      disabled={enrollment?.status === "completed"}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Standards Installed</Label>
                    <Textarea
                      value={completionForm.outcomes_summary.standards_installed}
                      onChange={(e) => setCompletionForm(prev => ({
                        ...prev,
                        outcomes_summary: { ...prev.outcomes_summary, standards_installed: e.target.value }
                      }))}
                      placeholder="Summary of standards installed..."
                      disabled={enrollment?.status === "completed"}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Momentum Activated</Label>
                    <Textarea
                      value={completionForm.outcomes_summary.momentum_activated}
                      onChange={(e) => setCompletionForm(prev => ({
                        ...prev,
                        outcomes_summary: { ...prev.outcomes_summary, momentum_activated: e.target.value }
                      }))}
                      placeholder="Summary of momentum activated..."
                      disabled={enrollment?.status === "completed"}
                    />
                  </div>
                </div>

                {enrollment?.status !== "completed" && enrollment?.session_3?.completed && (
                  <Button
                    onClick={handleCompleteEngagement}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    disabled={saving}
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Award className="w-4 h-4 mr-2" />}
                    Complete R.A.P.I.D.™ Engagement
                  </Button>
                )}

                {enrollment?.status === "completed" && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                    <Award className="w-10 h-10 text-green-500 mx-auto mb-3" />
                    <p className="text-green-800 font-semibold text-lg">Engagement Completed</p>
                    <p className="text-green-600">
                      Completed on {new Date(enrollment.completion_data?.completed_at).toLocaleDateString()}
                    </p>
                  </div>
                )}

                {!enrollment?.session_3?.completed && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                    <Clock className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
                    <p className="text-yellow-800">Complete all sessions first</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ConsultantRapidDetailPage;
